import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { ActivityIndicator, InteractionManager, Platform, Pressable, Text, TextInput, TouchableOpacity, View } from "react-native";
import ThemeProvider from "@voltmoney/theme";
import { BorderRadiusTokens, ColorTokens, FontFamilyTokens, FontSizeTokens, IconSizeTokens, IconTokens, InputStateToken, InputTypeToken, KeyboardTypeToken, SizeTypeTokens, StackAlignItems, StackHeight, StackJustifyContent, StackType, StackWidth, TextInputTypeToken, WIDGET } from "@voltmoney/schema";
import Stack from "../stack/Stack";
import Space from "../space/Space";
import { Typography } from "../typography/Typography";
import { Icon } from "../icon/Icon";
import debounce from "lodash/debounce";
import * as Animatable from 'react-native-animatable';
import { get } from "lodash";
import { priceInWords, priceInWordsContainsDecimal } from "../stack/utils";
import { isFromTDLPlatform } from "../pages/utils";
/**
 * Raw Component with Derived props + Theme
 */
const inputRefMap = {};
var FOCUS_TYPE;
(function (FOCUS_TYPE) {
    FOCUS_TYPE["NEXT"] = "next";
    FOCUS_TYPE["PREVIOUS"] = "previous";
    FOCUS_TYPE["CURRENT"] = "current";
})(FOCUS_TYPE || (FOCUS_TYPE = {}));
const TextInputDefault = ({ id, keyboardType = KeyboardTypeToken.default, nonZeroStartingNumberPattern = false, nonZeroStartingNumberPatternString = false, maxCharAllowed = 9999, width = TextInputTypeToken.FULL, type, state = InputStateToken.DEFAULT, value, valueInText, isDarkMode, multiline = false, onChangeText, title, clearEnabled = type === InputTypeToken.SEARCH, showPasswordIcon, togglePasswordIcon, toggleAction, leadingWidget = type === InputTypeToken.MOBILE ||
    type === InputTypeToken.MULTILINE
    ? {
        id: 'lead_widget',
        type: WIDGET.TEXT,
        props: type === InputTypeToken.MULTILINE
            ? {
                label: '₹',
                color: ColorTokens.Grey_Night,
                fontSize: FontSizeTokens.XXXXL,
                fontWeight: '400',
                fontFamily: FontFamilyTokens.Inter,
                padding: {
                    top: 30,
                },
            }
            : {
                label: '+91  ',
                color: ColorTokens.Grey_Smoke,
                fontSize: FontSizeTokens.MD,
            },
    }
    : null, action, triggerAction, caption, border = {
    color: ColorTokens.Grey_Smoke,
    radius: BorderRadiusTokens.BR1,
    size: 1,
    colorError: ColorTokens.System_Error,
    colorSuccess: ColorTokens.System_Happy,
    colorFocus: ColorTokens.Primary_100,
}, bgColor = isDarkMode ? ColorTokens.DARK_MODE : ColorTokens.White, 
//  bgColor =  ColorTokens.DARK_MODE,
regex = null, placeholder = type === InputTypeToken.SEARCH
    ? 'Type Keyword to Search'
    : '', charLimit = 0, limit = 0, limitLabel = limit ? `out of ${limit}` : '', errorAction, successAction, secureTextEntry = false, onPressAction, isFocus, clearAction, isUpperCase = false, isLowerCase = false, isSuccessIcon = true, errorCallback, successCallback, charLimitVisibility = true, getRef = null, onlyNumeric = false, alphaNumeric = false, alphaNumericSpace = false, metaData = null, delayedRequestFocus = true, loading, maxPayAmount, alignErrorInBottom, onPress, customWidth, preText, showErrorOnFocus, counter, showErrorAnimation, extraSpace, needSearchIcon, clearTextAction, showSuccessWithoutRegex = false }) => {
    const widgetId = get(metaData, 'widgetId', '');
    const routeId = get(metaData, 'routeId', '');
    const uniqueId = `${widgetId}-${routeId}`;
    const ref = useRef(null);
    const isPreFillTriggered = useRef(false);
    const [newValue, setValue] = useState(value || '');
    const [newState, setState] = useState(state);
    const [isFocusX, setFocus] = useState(false);
    const theme = ThemeProvider.getTheme();
    const colorMapping = theme.colors;
    const fontFamilyMapping = theme.fontFamily;
    const fontSizeMapping = theme.fontSize;
    const borderRadiusValue = theme.borderRadius[border.radius];
    const isfromTDL = isFromTDLPlatform();
    const colorChooseTitle = (state) => {
        switch (state) {
            case InputStateToken.ERROR:
                return (!isFocusX || showErrorOnFocus) ? ColorTokens.System_Error : isDarkMode ? ColorTokens.White : ColorTokens.Grey_Charcoal;
            case InputStateToken.DEFAULT:
            case InputStateToken.SUCCESS:
            default:
                return isDarkMode ? ColorTokens.White : ColorTokens.Grey_Charcoal;
        }
    };
    const requestFocusWithDelay = () => {
        const focusTimer = setTimeout(() => {
            clearTimeout(focusTimer);
            ref.current?.focus();
        }, delayedRequestFocus ? 300 : 0);
    };
    useEffect(() => {
        if (state === InputStateToken.DISABLED || state === InputStateToken.SELECTION) {
            return;
        }
        else if (state === InputStateToken.SUCCESS) {
            successCallback && successCallback();
            successAction &&
                triggerAction &&
                triggerAction(successAction);
        }
        else if (state === InputStateToken.ERROR) {
            errorCallback && errorCallback();
            errorAction &&
                triggerAction &&
                triggerAction(errorAction);
        }
        else if (state === InputStateToken.FOCUS) {
            requestFocusWithDelay();
        }
    }, [state]);
    useEffect(() => {
        if (value) {
            handleTextChange(value);
            if (newState !== InputStateToken.DISABLED)
                handleStateChange(value);
            isPreFillTriggered.current = true;
        }
    }, [value]);
    useEffect(() => {
        if (newState !== InputStateToken.DISABLED)
            handleStateChange(newValue);
    }, [isFocusX, newValue]);
    /**** only once and first time ***/
    useEffect(() => {
        InteractionManager.runAfterInteractions(() => {
            if (isFocus || ref.current?.focus)
                requestFocusWithDelay();
        });
    }, [isFocus]);
    const styleProps = {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        borderWidth: 1,
        backgroundColor: isDarkMode ? '#0D141F' : colorMapping[bgColor],
        borderColor: state === InputStateToken.ERROR
            ? (!isFocusX || showErrorOnFocus) ? colorMapping[border.colorError] : colorMapping[border.colorFocus]
            : isFocusX ? colorMapping[border.colorFocus] : colorMapping[border.color],
        borderRadius: isfromTDL ? 8 : borderRadiusValue,
        paddingVertical: 12,
        paddingRight: 16,
        paddingLeft: 12,
        marginTop: 8,
        width: customWidth && customWidth,
        height: type === InputTypeToken.MULTILINE ? 80 : 48,
    };
    const validate = (str) => {
        return str.match(regex);
    };
    const requestFocus = (focusType) => {
        const inputRefKeys = Object.keys(inputRefMap);
        inputRefKeys.forEach((ref, index) => {
            let calculatedIndex = 0;
            switch (focusType) {
                case FOCUS_TYPE.NEXT:
                    calculatedIndex = index + 1;
                    break;
                case FOCUS_TYPE.PREVIOUS:
                    calculatedIndex = index - 1;
                    break;
                default:
                    calculatedIndex = index;
                    break;
            }
            if (calculatedIndex < 0) {
                calculatedIndex = 0;
            }
            if (calculatedIndex >= inputRefKeys.length) {
                calculatedIndex = inputRefKeys.length - 1;
            }
            /*** find the next input ref and focus ***/
            const currRef = inputRefKeys[index];
            const nextRef = inputRefKeys[calculatedIndex];
            if (!currRef)
                return;
            if (!nextRef)
                return;
            if (!inputRefMap[currRef])
                return;
            if (!inputRefMap[nextRef])
                return;
            /*** find next input ref dynamically ***/
            if (uniqueId === ref) {
                nextRef &&
                    setTimeout(() => inputRefMap[nextRef].focus(), 16);
            }
        });
    };
    const handleStateChange = (text) => {
        if (limit > 0 && parseFloat(text) > limit || maxPayAmount > 0 && parseFloat(text) > maxPayAmount) {
            setState(InputStateToken.ERROR);
        }
        /// case: only regex
        else if (regex && charLimit === 0) {
            if (text.length === 0) {
                setState(InputStateToken.DEFAULT);
                return;
            }
            if (validate(text)) {
                if (state !== InputStateToken.SUCCESS) {
                    setState(InputStateToken.SUCCESS);
                }
                successCallback && successCallback();
                successAction &&
                    triggerAction &&
                    triggerAction(successAction);
            }
            else {
                setState(InputStateToken.ERROR);
                if (isFocusX) {
                    errorCallback && errorCallback();
                    errorAction &&
                        triggerAction &&
                        triggerAction(errorAction);
                }
            }
        }
        ///case: both regex and char limit
        else if (regex && charLimit > 0) {
            if (text.length === 0) {
                setState(InputStateToken.DEFAULT);
                return;
            }
            if (text.length === charLimit) {
                if (validate(text)) {
                    if (state !== InputStateToken.SUCCESS) {
                        setState(InputStateToken.SUCCESS);
                    }
                    successCallback && successCallback();
                    successAction &&
                        triggerAction &&
                        triggerAction(successAction);
                }
                else {
                    setFocus(false);
                    setState(InputStateToken.ERROR);
                }
            }
            else {
                setState(InputStateToken.ERROR);
                if (!isFocusX) {
                    setState(InputStateToken.ERROR);
                }
                if (isFocusX) {
                    errorCallback && errorCallback();
                    errorAction &&
                        triggerAction &&
                        triggerAction(errorAction);
                }
            }
        }
        ///case: only char limit
        else {
            if (charLimit === 0 &&
                text.length > charLimit) {
                setState(InputStateToken.SUCCESS);
            }
            else if (charLimit > 0 &&
                text.length > charLimit) {
                setState(InputStateToken.ERROR);
            }
            else if (charLimit > 0 &&
                text.length === charLimit) {
                setState(InputStateToken.SUCCESS);
            }
            else if (newValue.length === 0) {
                isFocusX
                    ? setState(InputStateToken.FOCUS)
                    : setState(InputStateToken.DEFAULT);
            }
            else {
                setState(InputStateToken.ERROR);
            }
        }
    };
    const handleTextChange = (text) => {
        if (alphaNumericSpace) {
            text = text.startsWith('+') ? '+' + text.slice(1).replace(/[^A-Za-z0-9 ]/g, '') : text.replace(/[^A-Za-z0-9 ]/g, '');
        }
        if (limit > 0 && parseFloat(text) > limit) {
            return;
        }
        if (nonZeroStartingNumberPatternString && text) {
            text = String(text)
                .replace(/[^0-9.]/g, '') // Remove non-numeric and non-decimal characters
                .replace(/(^0+)(?=\d)/, '') // Remove leading zeros unless followed by a decimal point
                .replace(/(\.\d{2})(\d+)/, '$1') // Limit to two decimal places
                .replace(/(\.(?=\d*\.\d*))/g, '') // Remove extra decimals
                .replace(/(\.\d+?)(\.\d+)/g, '$1'); // Preserve only the first decimal
        }
        if (nonZeroStartingNumberPattern && text) {
            text = String(text)?.replace(/(^0(?!\.)|[^0-9.])|(\.(?=\d*\.\d*))|(\.\d{3,})(?=\d)/g, (match, offset) => {
                if (match.startsWith('.'))
                    return match.slice(0, 1); // Keep the first decimal
                return ''; // Remove extra characters
            }).replace(/(\.\d{2})(\d+)/, '$1');
        }
        else if (isUpperCase && text) {
            text = text.toUpperCase();
        }
        else if (isLowerCase && text) {
            text = text.toLowerCase();
        }
        else if (onlyNumeric && text && id !== 'tf_withdrawAmount_enterAmount') {
            text = text.replace(/[^0-9]/g, '');
        }
        else if (onlyNumeric && text && id === 'tf_withdrawAmount_enterAmount') {
            text = text.replace(/[^0-9.]+/g, '');
            text = text.replace(/\.{2,}/g, '.');
            text = text.replace(/^0*(\d+(\.\d{0,2})?).*$/, '$1');
        }
        if (alphaNumeric) {
            text = text.replace(/[^A-Za-z0-9]/g, '');
        }
        setValue(text);
        onChangeText && onChangeText(text);
        action &&
            triggerAction &&
            debounce(triggerAction, 250)({
                ...action,
                payload: {
                    ...action.payload,
                    value: text,
                },
            });
    };
    const setRef = (refX) => {
        ref.current = refX;
        if (getRef)
            getRef(refX);
        inputRefMap[uniqueId] = refX;
    };
    const renderCaption = useCallback(() => !caption ? (React.createElement(React.Fragment, null)) : (React.createElement(Stack, null,
        React.createElement(Space, { size: SizeTypeTokens.MD }),
        React.createElement(Stack, { type: StackType.row },
            (newState === InputStateToken.DEFAULT || state === InputStateToken.DEFAULT) &&
                caption.default && (React.createElement(Typography, { label: caption.default, fontSize: FontSizeTokens.XS, color: ColorTokens.Primary_100, fontWeight: '400' })),
            (newState === InputStateToken.SUCCESS || state === InputStateToken.SUCCESS) &&
                caption.success && (React.createElement(Typography, { label: caption.success, fontSize: FontSizeTokens.XS, color: ColorTokens.System_Happy, fontWeight: '400' })),
            (newState === InputStateToken.ERROR || state === InputStateToken.ERROR) &&
                caption.error &&
                (id !== "tf_setCreditLimit_creditLimit" && id !== "tf_flexiRepay_enterAmount") &&
                (!isFocusX || showErrorOnFocus) && (showErrorAnimation ? (React.createElement(Animatable.View, { animation: 'fadeIn', delay: 100, duration: 500 },
                React.createElement(Typography, { label: caption.error, fontSize: FontSizeTokens.XS, color: ColorTokens.System_Error, fontWeight: '400' }))) : (React.createElement(Typography, { label: caption.error, fontSize: FontSizeTokens.XS, color: ColorTokens.System_Error, fontWeight: '400' }))),
            (isFocusX || (!isFocusX && newState !== InputStateToken.ERROR)) && (React.createElement(View, { style: { height: extraSpace } })),
            (newState === InputStateToken.ERROR || state === InputStateToken.ERROR) &&
                caption.error &&
                (id === "tf_setCreditLimit_creditLimit" || id === 'tf_flexiRepay_enterAmount') && (React.createElement(Typography, { label: caption.error, fontSize: FontSizeTokens.XS, color: ColorTokens.System_Error, fontWeight: '400' }))))), [caption, newState, state, isFocusX, showErrorOnFocus, counter]);
    const focusInputWithKeyboard = () => {
        InteractionManager.runAfterInteractions(() => {
            ref.current.focus();
        });
    };
    const renderContent = () => {
        return (React.createElement(Pressable, { style: [
                styleProps,
                width === TextInputTypeToken.FULL
                    ? { width: customWidth ? customWidth : '100%' }
                    : { flex: 1 },
            ], onPress: async () => {
                onPress && onPress();
                if (onPressAction) {
                    triggerAction(onPressAction);
                }
                else {
                    if (state !== InputStateToken.DISABLED) {
                        requestFocusWithDelay();
                        setState(InputStateToken.FOCUS);
                    }
                }
            } },
            React.createElement(View, { style: {
                    position: 'absolute',
                    left: 12,
                    top: -8,
                    backgroundColor: isDarkMode ? colorMapping[ColorTokens.DARK_MODE] : 'white',
                    paddingHorizontal: 4,
                } },
                React.createElement(Typography, { label: title, fontSize: FontSizeTokens.XS, color: colorChooseTitle(state), fontWeight: '400' })),
            React.createElement(View, { style: {
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: limitLabel ? '100%' : '100%'
                } },
                React.createElement(Stack, { type: StackType.row, justifyContent: StackJustifyContent.spaceBetween, alignItems: type === InputTypeToken.MULTILINE ? StackAlignItems.flexStart : StackAlignItems.center, flex: 1 },
                    leadingWidget &&
                        type === InputTypeToken.MULTILINE && (React.createElement(View, { style: { marginTop: 6 } },
                        React.createElement(Typography, { ...leadingWidget.props }))),
                    type === InputTypeToken.MULTILINE && (React.createElement(Space, { size: SizeTypeTokens.LG })),
                    React.createElement(Stack, { type: type === InputTypeToken.MULTILINE ? StackType.column : StackType.row, justifyContent: StackJustifyContent.flexStart, height: StackHeight.MATCH_PARENT, alignItems: type === InputTypeToken.MULTILINE ? StackAlignItems.flexStart : StackAlignItems.center, flex: 1 },
                        React.createElement(Space, { size: SizeTypeTokens.SM }),
                        leadingWidget &&
                            type !== InputTypeToken.MULTILINE && (React.createElement(View, { style: { alignContent: 'center' } }, preText ? (React.createElement(React.Fragment, null,
                            React.createElement(Typography, { label: preText, color: ColorTokens.Grey_Smoke, fontSize: FontSizeTokens.MD }))) : (React.createElement(React.Fragment, null,
                            React.createElement(Typography, { ...leadingWidget.props }))))),
                        React.createElement(Space, { size: SizeTypeTokens.XS }),
                        newState === InputStateToken.DISABLED || state === InputStateToken.DISABLED ? (React.createElement(Typography, { label: `${newValue || placeholder}`, color: newValue
                                ? isDarkMode ? ColorTokens.White : ColorTokens.Grey_Smoke
                                : isDarkMode ? ColorTokens.White : ColorTokens.Grey_Smoke, fontSize: FontSizeTokens.MD, fontFamily: FontFamilyTokens.Inter, fontWeight: '400' })) : newState === InputStateToken.SELECTION || state === InputStateToken.SELECTION ? (React.createElement(Typography, { label: `${newValue || placeholder}`, color: isDarkMode ? ColorTokens.DARK_MODE : ColorTokens.Grey_Night, fontSize: FontSizeTokens.MD, fontFamily: FontFamilyTokens.Inter, fontWeight: '400' })) : (React.createElement(TextInput, { pointerEvents: onPressAction ? 'none' : 'auto', value: newValue, maxLength: charLimit ? charLimit : maxCharAllowed, secureTextEntry: secureTextEntry, ref: setRef, autoFocus: isFocus, 
                            //@ts-ignore
                            editable: state !== InputStateToken.DISABLED || newState !== InputStateToken.DISABLED, placeholder: placeholder, placeholderTextColor: colorMapping[ColorTokens.Grey_300], placeholderStyle: { padding: 0, alignContent: 'center', fontSize: FontSizeTokens.MD }, onChangeText: handleTextChange, nativeID: id, underlineColorAndroid: 'transparent', style: [
                                {
                                    color: isDarkMode ? colorMapping[ColorTokens.White] :
                                        !isFocusX && colorMapping[ColorTokens.Grey_Night],
                                    fontSize: type === InputTypeToken.MULTILINE
                                        ? fontSizeMapping[FontSizeTokens.XXXXL]
                                        : fontSizeMapping[FontSizeTokens.MD],
                                    fontFamily: fontFamilyMapping.Inter,
                                    fontWeight: '400',
                                    flexDirection: 'row',
                                    width: '100%',
                                    padding: 0,
                                    justifyContent: 'center',
                                },
                                Platform.OS === 'web' && {
                                    //@ts-ignore
                                    outlineStyle: 'none',
                                },
                            ], textAlignVertical: multiline ? 'top' : 'center', onFocus: () => {
                                //@ts-ignore
                                if (state !== InputStateToken.DISABLED || newState !== InputStateToken.DISABLED) {
                                    setFocus(true);
                                    setState(InputStateToken.FOCUS);
                                }
                            }, onBlur: () => {
                                setFocus(false);
                                handleStateChange(newValue);
                            }, autoCapitalize: isUpperCase ? 'characters' : 'none', keyboardType: keyboardType, 
                            //disabled={isDisabled}
                            onKeyPress: (e) => {
                                if (e.nativeEvent.key === 'Tab') {
                                    /*** if tab is pressed, focus on next input ***/
                                    requestFocus(FOCUS_TYPE.NEXT);
                                } /*else if (
                                e.nativeEvent.key === 'Backspace'
                            ) {
                                /!*** if tab is pressed, focus on next input ***!/
                                requestFocus(FOCUS_TYPE.PREVIOUS);
                            }*/
                            } })),
                        React.createElement(Pressable, { onPress: () => requestFocusWithDelay(), style: { position: 'absolute', right: 0 } },
                            React.createElement(Stack, { width: StackWidth.CONTENT, height: StackHeight.MATCH_PARENT, alignItems: StackAlignItems.center, justifyContent: StackJustifyContent.center },
                                React.createElement(Stack, { justifyContent: StackJustifyContent.center, alignItems: StackAlignItems.flexEnd },
                                    clearEnabled && !needSearchIcon &&
                                        `${newValue}`.length > 0 &&
                                        isFocusX &&
                                        !loading &&
                                        newState !== InputStateToken.SUCCESS && (React.createElement(React.Fragment, null,
                                        React.createElement(Pressable, { onPress: async () => {
                                                ref.current.clear();
                                                setValue('');
                                                if (clearAction) {
                                                    await triggerAction(clearAction);
                                                }
                                            } }, newValue ? (React.createElement(Icon, { name: IconTokens.Cross, color: ColorTokens.Grey_Smoke, size: IconSizeTokens.LG })) : (React.createElement(Icon, { name: IconTokens.Search, color: ColorTokens.Grey_Smoke, size: IconSizeTokens.LG }))),
                                        React.createElement(Space, { size: SizeTypeTokens.XS }))),
                                    needSearchIcon && clearEnabled && (React.createElement(TouchableOpacity, { onPress: async () => {
                                            ref.current.clear();
                                            setValue('');
                                            clearTextAction();
                                        } },
                                        React.createElement(Icon, { name: newValue.length === 0 ? IconTokens.Search : IconTokens.Cross, color: ColorTokens.Grey_Smoke, size: IconSizeTokens.LG }))),
                                    limitLabel && !loading && (React.createElement(Typography, { fontSize: FontSizeTokens.XXS, color: ColorTokens.Grey_Smoke, label: limitLabel })),
                                    charLimitVisibility ? (charLimit > 0 &&
                                        newValue.length > 0 &&
                                        !loading &&
                                        newValue.length < charLimit && (React.createElement(Typography, { fontSize: FontSizeTokens.XXS, color: ColorTokens.Grey_Smoke, label: `${newValue.length}/${charLimit}` }))) : (React.createElement(React.Fragment, null)),
                                    (newState === InputStateToken.SUCCESS || state === InputStateToken.SUCCESS) &&
                                        (regex || showSuccessWithoutRegex) &&
                                        type !== InputTypeToken.SEARCH &&
                                        !loading &&
                                        isSuccessIcon && (React.createElement(Icon, { size: IconSizeTokens.XL, color: ColorTokens.System_Happy, name: IconTokens.Done })),
                                    showPasswordIcon && (React.createElement(Pressable, { onPress: handlePasswordToggle }, togglePasswordIcon ?
                                        React.createElement(Icon, { size: IconSizeTokens.XL, color: ColorTokens.System_Happy, name: IconTokens.PasswordHide }) : React.createElement(Icon, { size: IconSizeTokens.XL, color: ColorTokens.System_Happy, name: IconTokens.PasswordShow }))),
                                    (loading || newState === InputStateToken.LOADING) && (React.createElement(ActivityIndicator, { size: 16, style: {
                                            height: 16,
                                            width: 16,
                                        }, color: theme.colors[ColorTokens.Primary_100] }))))),
                        React.createElement(Space, { size: SizeTypeTokens.SM }))))));
    };
    const handlePasswordToggle = () => {
        toggleAction &&
            triggerAction &&
            triggerAction(toggleAction);
    };
    return (React.createElement(React.Fragment, null, alignErrorInBottom ? (React.createElement(View, { style: { flex: 1, flexDirection: 'column', width: '100%' } },
        renderContent(),
        type === InputTypeToken.MULTILINE && (React.createElement(Text, { style: {
                alignSelf: 'flex-start',
                color: '#667085',
                marginTop: 16,
            } }, priceInWords(newValue))),
        renderCaption())) : (React.createElement(React.Fragment, null,
        renderContent(),
        (type === InputTypeToken.MULTILINE && id === 'tf_withdrawAmount_enterAmount') && (React.createElement(Text, { style: {
                alignSelf: 'flex-start',
                color: '#667085',
                marginTop: 16,
            } }, priceInWordsContainsDecimal(newValue))),
        (type === InputTypeToken.MULTILINE && id !== 'tf_withdrawAmount_enterAmount') && (React.createElement(Text, { style: {
                alignSelf: 'flex-start',
                color: '#667085',
                marginTop: 16,
            } }, priceInWords(newValue))),
        renderCaption()))));
};
export default memo(TextInputDefault);
export { TextInputDefault };
